import { useState, useEffect, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pro Desks Tab
 * --------------------------------------------------------------------------
 * One-click materials cart send to Home Depot Pro Xtra / Lowe's for Pros / Wayfair Pro.
 * Shannon's Pro accounts are pre-configured ("Moovin Forward" Member X at HD).
 * - Configure Pro Desk email per store once
 * - Pick a project, hit "Send to HD Pro / Lowe's Pro / Wayfair Pro" — CSV emails out
 * - Download CSV manually if she wants to paste into the Pro app instead
 */

const STORE_META = {
  home_depot: { label: "Home Depot Pro Xtra", color: "#F96302", icon: "🏠", subtitle: "Quick Add via Pro Xtra" },
  lowes: { label: "Lowe's For Pros", color: "#004990", icon: "🔵", subtitle: "Saved List / Pro Rewards" },
  wayfair: { label: "Wayfair Professional", color: "#7B189F", icon: "💜", subtitle: "B2B Pro list import" },
};

const ProDesksTab = () => {
  const [stores, setStores] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [productCount, setProductCount] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStore, setEditingStore] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, kind = "success") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  const loadStores = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/pro-desk/config`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setStores(d.stores || []);
      }
    } catch {}
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        const list = Array.isArray(d) ? d : (d.projects || []);
        setProjects(list);
        if (list.length > 0 && !selectedProject) setSelectedProject(list[0].project_id);
      }
    } catch {}
  }, [selectedProject]);

  const loadProductCount = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/sourced-products`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setProductCount((d.products || []).length);
      }
    } catch {}
  }, []);

  useEffect(() => {
    Promise.all([loadStores(), loadProjects(), loadProductCount()]).finally(() => setLoading(false));
  }, [loadStores, loadProjects, loadProductCount]);

  const saveStore = async (storeKey, email, label, notes) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/pro-desk/config`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ store_key: storeKey, pro_desk_email: email, store_label: label, notes }),
      });
      if (r.ok) {
        showToast("Pro Desk saved ✓");
        await loadStores();
        setEditingStore(null);
      } else {
        const err = await r.json().catch(() => ({}));
        showToast(err.detail || "Save failed", "error");
      }
    } catch {
      showToast("Save failed", "error");
    }
  };

  const sendToProDesk = async (storeKey) => {
    if (!selectedProject) { showToast("Pick a project first", "error"); return; }
    if (productCount === 0) { showToast("Add products in My Products first", "error"); return; }
    if (!window.confirm(`Send the current sourced-products list to ${STORE_META[storeKey].label}?`)) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/projects/${selectedProject}/pro-desk/send`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ store_key: storeKey, note: "" }),
      });
      const d = await r.json();
      if (!r.ok || d.ok === false) {
        showToast(d.detail || d.error || "Send failed", "error");
      } else {
        showToast(`✓ Sent to ${STORE_META[storeKey].label}`);
      }
    } catch {
      showToast("Send failed", "error");
    }
  };

  const downloadCsv = (storeKey) => {
    if (!selectedProject) { showToast("Pick a project first", "error"); return; }
    window.open(`${BACKEND_URL}/api/admin/projects/${selectedProject}/pro-desk/csv?store=${storeKey}`, "_blank");
  };

  return (
    <div data-testid="pro-desks-tab" style={{ padding: "0 0.5rem" }}>
      {toast && (
        <div data-testid="pd-toast" style={{
          position: "fixed", top: 80, right: 20, zIndex: 1000,
          background: toast.kind === "error" ? "#7f1d1d" : "#064e3b",
          color: "#fff", padding: "0.85rem 1.25rem", borderRadius: 8,
          border: `1px solid ${toast.kind === "error" ? "#ef4444" : "#10b981"}`,
          fontSize: "0.86rem", fontWeight: 700, maxWidth: 360,
        }}>{toast.msg}</div>
      )}

      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ color: "#fff", fontSize: "1.65rem", fontWeight: 800, margin: "0 0 0.4rem" }}>
          Pro Desks · One-Click Cart
        </h1>
        <p style={{ color: "#ADBED1", fontSize: "0.92rem", margin: 0, lineHeight: 1.55, maxWidth: 780 }}>
          Push your <strong style={{ color: "#fff" }}>My Products</strong> list straight to Home Depot Pro Xtra, Lowe&apos;s For Pros, or Wayfair Professional. The system formats a Pro-compatible CSV and emails it to the Pro Desk — no copy-paste, no scanning.
        </p>
      </div>

      {/* Project + product count strip */}
      <div style={{ display: "flex", gap: "0.85rem", flexWrap: "wrap", alignItems: "center", marginBottom: "1.25rem", padding: "0.85rem 1.1rem", background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <label style={{ display: "block", color: "#ADBED1", fontSize: "0.72rem", fontWeight: 700, marginBottom: "0.3rem", letterSpacing: "0.04em", textTransform: "uppercase" }}>Project</label>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} data-testid="pd-project" style={inputStyle}>
            <option value="" style={{ background: "#030B1A" }}>— Pick a project —</option>
            {projects.map(p => (
              <option key={p.project_id} value={p.project_id} style={{ background: "#030B1A" }}>
                {p.address}{p.unit_number ? ` #${p.unit_number}` : ""} — {p.client_name || "—"}
              </option>
            ))}
          </select>
        </div>
        <div style={{ minWidth: 180 }}>
          <label style={{ display: "block", color: "#ADBED1", fontSize: "0.72rem", fontWeight: 700, marginBottom: "0.3rem", letterSpacing: "0.04em", textTransform: "uppercase" }}>Products on file</label>
          <div data-testid="pd-product-count" style={{ color: productCount > 0 ? "#00DC78" : "#F59E0B", fontWeight: 800, fontSize: "1.15rem" }}>
            {productCount} item{productCount === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* Pro Desk store cards */}
      {loading && <p style={{ color: "#ADBED1" }}>Loading…</p>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: "0.95rem" }}>
        {stores.map(s => {
          const meta = STORE_META[s.store_key] || { label: s.label, color: "#94A3B8", icon: "🛠" };
          const isEditing = editingStore === s.store_key;
          return (
            <div key={s.store_key} data-testid={`pd-card-${s.store_key}`} style={{
              padding: "1.1rem 1.25rem", background: "rgba(255,255,255,0.025)",
              border: `1px solid ${s.is_configured ? meta.color : "rgba(255,255,255,0.10)"}`,
              borderRadius: 10,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem" }}>
                <div style={{ fontSize: "1.5rem" }}>{meta.icon}</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, margin: 0 }}>{meta.label}</h3>
                  <p style={{ color: "#ADBED1", fontSize: "0.74rem", margin: "2px 0 0" }}>{meta.subtitle}</p>
                </div>
                <span style={{
                  padding: "0.15rem 0.5rem", borderRadius: 4, fontSize: "0.65rem", fontWeight: 800,
                  background: s.is_configured ? "rgba(0,220,120,0.15)" : "rgba(255,255,255,0.06)",
                  color: s.is_configured ? "#00DC78" : "#7E92A8",
                  border: `1px solid ${s.is_configured ? "rgba(0,220,120,0.30)" : "rgba(255,255,255,0.12)"}`,
                }}>{s.is_configured ? "READY" : "SET UP"}</span>
              </div>

              {!isEditing ? (
                <>
                  <div style={{ padding: "0.55rem 0.7rem", background: "rgba(0,0,0,0.20)", borderRadius: 6, marginBottom: "0.65rem", fontSize: "0.78rem", color: s.pro_desk_email ? "#fff" : "#7E92A8" }}>
                    {s.pro_desk_email || "No email set — click Configure"}
                  </div>
                  {s.store_label && <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginBottom: "0.4rem" }}>📍 {s.store_label}</div>}
                  <div style={{ color: "#7E92A8", fontSize: "0.72rem", lineHeight: 1.45, marginBottom: "0.85rem" }}>{s.instructions}</div>
                  <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap" }}>
                    <button
                      onClick={() => sendToProDesk(s.store_key)}
                      data-testid={`pd-send-${s.store_key}`}
                      disabled={!s.is_configured || !selectedProject || productCount === 0}
                      style={{
                        flex: 1,
                        background: (s.is_configured && selectedProject && productCount > 0) ? meta.color : "rgba(255,255,255,0.08)",
                        color: "#fff", border: "none", padding: "0.55rem 0.85rem", borderRadius: 6,
                        fontSize: "0.78rem", fontWeight: 800,
                        cursor: (s.is_configured && selectedProject && productCount > 0) ? "pointer" : "not-allowed",
                        opacity: (s.is_configured && selectedProject && productCount > 0) ? 1 : 0.5,
                      }}
                    >
                      📤 Send Cart
                    </button>
                    <button
                      onClick={() => downloadCsv(s.store_key)}
                      data-testid={`pd-csv-${s.store_key}`}
                      style={ghostBtn}
                    >
                      ⬇ CSV
                    </button>
                    <button
                      onClick={() => setEditingStore(s.store_key)}
                      data-testid={`pd-edit-${s.store_key}`}
                      style={ghostBtn}
                    >
                      ⚙
                    </button>
                  </div>
                </>
              ) : (
                <StoreEditor store={s} onSave={saveStore} onCancel={() => setEditingStore(null)} />
              )}
            </div>
          );
        })}
      </div>

      {/* Help card */}
      <div style={{
        marginTop: "1.75rem", padding: "1.1rem 1.4rem",
        background: "linear-gradient(135deg, rgba(245,158,11,0.06) 0%, rgba(0,255,255,0.06) 100%)",
        border: "1px solid rgba(245,158,11,0.30)", borderRadius: 12,
      }}>
        <h3 style={{ color: "#F59E0B", fontSize: "0.95rem", fontWeight: 800, margin: "0 0 0.4rem" }}>
          💡 How to find your store&apos;s Pro Desk email
        </h3>
        <ul style={{ color: "#ADBED1", fontSize: "0.82rem", lineHeight: 1.65, margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
          <li><strong style={{ color: "#fff" }}>Home Depot:</strong> Visit your local store&apos;s Pro Desk in person once. They&apos;ll give you a direct store email (often <code style={{ color: "#FCA5A5" }}>prodesk[storenumber]@homedepot.com</code>). Your account is already &ldquo;MOOVIN FORWARD&rdquo; on Pro Xtra.</li>
          <li><strong style={{ color: "#fff" }}>Lowe&apos;s:</strong> The reply-to address on your &ldquo;MyLowe&apos;s Pro Rewards&rdquo; emails is your account&apos;s unique inbox. Use that, OR sign in to lowesforpros.com → Account → contact your Pro account manager.</li>
          <li><strong style={{ color: "#fff" }}>Wayfair Pro:</strong> Reply to <code style={{ color: "#FCA5A5" }}>editor@members.wayfair.com</code> to be assigned a dedicated Pro rep — they&apos;ll give you a direct order email.</li>
        </ul>
      </div>
    </div>
  );
};

// =============================================================================
// Store editor
// =============================================================================
const StoreEditor = ({ store, onSave, onCancel }) => {
  const [email, setEmail] = useState(store.pro_desk_email || "");
  const [label, setLabel] = useState(store.store_label || "");
  const [notes, setNotes] = useState(store.notes || "");
  return (
    <div data-testid={`pd-editor-${store.store_key}`}>
      <input value={email} onChange={e => setEmail(e.target.value)} data-testid={`pd-editor-email-${store.store_key}`} placeholder="prodesk@store.com" style={{ ...inputStyle, marginBottom: 6 }} />
      <input value={label} onChange={e => setLabel(e.target.value)} data-testid={`pd-editor-label-${store.store_key}`} placeholder="Store label (e.g., FW #6526)" style={{ ...inputStyle, marginBottom: 6 }} />
      <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes" rows={2} style={{ ...inputStyle, fontFamily: "inherit", marginBottom: 8 }} />
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => onSave(store.store_key, email, label, notes)} data-testid={`pd-editor-save-${store.store_key}`} style={{
          flex: 1, background: "linear-gradient(135deg,#00DC78 0%,#00FFFF 100%)", color: "#030B1A",
          border: "none", padding: "0.5rem 0.85rem", borderRadius: 6, fontWeight: 800, cursor: "pointer", fontSize: 12,
        }}>Save</button>
        <button onClick={onCancel} data-testid={`pd-editor-cancel-${store.store_key}`} style={ghostBtn}>Cancel</button>
      </div>
    </div>
  );
};

const inputStyle = {
  width: "100%", padding: "0.5rem 0.7rem", borderRadius: 6,
  border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.04)",
  color: "#fff", fontSize: "0.8rem", boxSizing: "border-box",
};
const ghostBtn = {
  background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.20)",
  padding: "0.5rem 0.8rem", borderRadius: 6, fontSize: "0.75rem", fontWeight: 700, cursor: "pointer",
};

export default ProDesksTab;
